import { useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Hourglass, XCircle } from "lucide-react";
import { renderText } from "@/lib/renderText";

// RC50 V5-H4 UX-007 / ACC-002 / ACC-004 / ACC-005 — presentation-only helpers.
// Nothing here alters conversation data, model context, cards, or transaction state.

export const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-1";

export const LONG_TEXT_LIMIT = 600;

export function LongText({ text, limit = LONG_TEXT_LIMIT, testId = "copilot-long-text" }) {
  const [open, setOpen] = useState(false);
  const full = text || "";
  if (full.length <= limit) return <p className="whitespace-pre-wrap break-words">{renderText(full)}</p>;
  const cut = full.lastIndexOf(" ", limit);
  const preview = full.slice(0, cut > limit * 0.6 ? cut : limit).trimEnd();
  return (
    <div data-testid={testId} data-expanded={open}>
      <p className="whitespace-pre-wrap break-words">{renderText(open ? full : `${preview}…`)}</p>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className={`mt-1 text-[11px] font-medium text-terracotta hover:underline ${FOCUS_RING}`} data-testid="copilot-show-more">
        {open ? "Show less" : `Show more (${full.length - preview.length} more characters)`}
      </button>
    </div>
  );
}

export function CardList({ items, initial = 3, noun = "result", render }) {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, initial);
  const hidden = items.length - shown.length;
  return (
    <>
      {shown.map(render)}
      {items.length > initial && (
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className={`justify-self-start text-[11px] font-medium text-terracotta hover:underline ${FOCUS_RING}`} data-testid="copilot-cards-show-more">
          {open ? `Show fewer ${noun}s` : `Show ${hidden} more ${noun}${hidden > 1 ? "s" : ""}`}
        </button>
      )}
    </>
  );
}

export const STATUS_META = {
  PREPARED: { label: "Prepared", Icon: Clock, tone: "text-charcoal" },
  EXECUTED: { label: "Executed", Icon: CheckCircle2, tone: "text-green-700" },
  FAILED: { label: "Failed", Icon: AlertTriangle, tone: "text-red-700" },
  DISCARDED: { label: "Discarded", Icon: XCircle, tone: "text-warmgrey" },
  CONFIRMED_AWAITING_EXECUTION: { label: "Confirmed · awaiting workflow", Icon: Hourglass, tone: "text-charcoal" },
  EXPIRED: { label: "Expired", Icon: XCircle, tone: "text-warmgrey" },
};

export function statusLabel(status) {
  return STATUS_META[status]?.label || status;
}

export function StatusBadge({ status, label, testId = "copilot-action-status" }) {
  const m = STATUS_META[status] || { label: status, Icon: Clock, tone: "text-warmgrey" };
  const { Icon } = m;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide ${m.tone}`}
      data-testid={testId} data-status={status} data-status-label={label || m.label}>
      <Icon className="h-3 w-3" aria-hidden="true" />{label || m.label}
    </span>
  );
}

// ACC-005: one polite region, text replaced (never appended) so only the newest change is read.
export function LiveRegion({ message }) {
  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="copilot-live-region">{message}</div>
  );
}
